import {
  VStack,
  Button,
  Text,
  HStack,
  Icon,
  Box,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Input,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { MdTableChart, MdContentCopy, MdDownload, MdUpload } from 'react-icons/md';
import { FiCpu } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useChatbot, useChatbots } from '@/lib/sdk/chatbot/queries';
import { ChatbotApi, transformTaskFromApiPayload } from '@/lib/sdk/chatbot/api';
import { useState, useRef } from 'react';
import { Task } from '@/lib/sdk/chatbot/types';
import { Xid } from 'xid-ts';
import { Modal } from '@/client/components/shared/Modal';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { helpers } from '@/client/utils/helpers';

interface CreateMethodDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
}

export const CreateMethodDialog = ({ isOpen, onClose, orgId }: CreateMethodDialogProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [selectedChatbotId, setSelectedChatbotId] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { user, currentOrg, userPermission, featurePermissions } = useAuth();
  const isSuperAdmin = user?.is_super_admin ?? false;
  // Org must have AI-assisted workflows enabled at all, and the user must
  // separately hold the workflows:ai_assist grant — org enablement alone
  // does not imply every user can use it (#681/#721 Phase 7a).
  const canUseAI = isSuperAdmin || (
    helpers.hasFeatureAccess(currentOrg?.feature_access, 'ai_chat') &&
    helpers.hasFeatureAction(featurePermissions, userPermission, isSuperAdmin, 'workflows', 'ai_assist')
  );

  // Fetch existing chatbots
  const { data: chatbotsData, isLoading } = useChatbots({ 
    orgId: orgId,
    limit: 100,
    active_only: true,
  });

  const { data: chatbot, isLoading: isSelectedLoading } = useChatbot(selectedChatbotId || '');

  const handleMethodSelect = (path: string) => {
    // If a chatbot is selected, pass its tasks as state
    if (selectedChatbotId) {
      if (chatbot) {
        // Re-assign new IDs for copied tasks (recursive)
        const reIdTask = (task: Task): Task => ({
          ...task,
          id: new Xid().toString(),
          subtasks: task.subtasks?.map(reIdTask) ?? [],
        });

        const formattedTasks: Task[] = chatbot.task_list
          .map(transformTaskFromApiPayload)
          .map(reIdTask);
        
        navigate(path, { 
          state: { 
            copiedTasks: formattedTasks 
          } 
        });
      } else {
        toast({
          title: "Error",
          description: "Selected chatbot not found",
          status: "error",
          duration: 3000,
        });
      }
    } else {
      // If no chatbot selected, just navigate
      navigate(path);
    }
    onClose();
  };

  const handleExport = async () => {
    if (!chatbot || !selectedChatbotId) {
      toast({
        title: "Error",
        description: "Please select a chatbot to export",
        status: "error",
        duration: 3000,
      });
      return;
    }

    try {
      const formattedTasks: Task[] = chatbot.task_list.map(transformTaskFromApiPayload);

      // Fetch linked_workflows metadata (non-critical — export proceeds without it on failure)
      let linkedWorkflows: Record<string, any> | undefined;
      try {
        const linksResponse = await ChatbotApi.getLinks(chatbot.short_code);
        if (linksResponse.links && Object.keys(linksResponse.links).length > 0) {
          linkedWorkflows = linksResponse.links;
        }
      } catch {
        // linked_workflows are optional — continue without them
      }

      const exportData: Record<string, any> = {
        title: chatbot.title,
        template: chatbot.template,
        tasks: formattedTasks,
        exportedAt: new Date().toISOString(),
        version: "1.0",
      };

      if (linkedWorkflows) {
        exportData.linked_workflows = linkedWorkflows;
      }

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${chatbot.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_export.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Chatbot configuration exported successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export chatbot configuration",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      toast({
        title: "Invalid File",
        description: "Please select a valid JSON file",
        status: "error",
        duration: 3000,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const importData = JSON.parse(content);

        // Validate the imported data structure
        if (!importData.tasks || !Array.isArray(importData.tasks)) {
          throw new Error("Invalid file format: tasks array not found");
        }

        // Recursive function to format imported tasks.
        // Exported files use camelCase Task format — pass all fields through and generate new IDs.
        const formatImportedTask = (task: any): Task => ({
          id: new Xid().toString(),
          name: task.name || '',
          description: task.description || '',
          type: task.type || '',
          data: task.data || '',
          actions: Array.isArray(task.actions) ? task.actions : [],
          mutable: task.mutable !== undefined ? task.mutable : true,
          maskOutput: task.maskOutput !== undefined ? task.maskOutput : false,
          requirePrefill: task.requirePrefill === true,
          softTtlValue: task.softTtlValue,
          softTtlUnit: task.softTtlUnit,
          documentLinks: Array.isArray(task.documentLinks) ? task.documentLinks : [],
          tags: Array.isArray(task.tags) ? task.tags : [],
          validationConfig: task.validationConfig,
          // Spread rather than re-list fields: an explicit list here silently drops any
          // field the exporter writes but the list forgot (see #670 — matchMode,
          // lookupField(s), titleField, descriptionField, valueSourceTaskId). The save
          // path already whitelists per task type and action.
          approvalConfig: task.approvalConfig ? { ...task.approvalConfig } : undefined,
          dataSourceConfig: task.dataSourceConfig ? {
            ...task.dataSourceConfig,
            source: task.dataSourceConfig.source || '',
          } : undefined,
          extractionConfig: task.extractionConfig ? { ...task.extractionConfig } : undefined,
          counterConfig: task.counterConfig ? { ...task.counterConfig } : undefined,
          autoFillConfig: task.autoFillConfig,
          startnewConfig: task.startnewConfig ? {
            targetChatbot: task.startnewConfig.targetChatbot,
            targetMappingSource: task.startnewConfig.targetMappingSource,
            targetTask: task.startnewConfig.targetTask,
            defaultTargetKey: task.startnewConfig.defaultTargetKey,
            attributeMapping: task.startnewConfig.attributeMapping,
          } : undefined,
          skipConfig: task.skipConfig ? {
            conditions: task.skipConfig.conditions ?? [],
            conditionLogic: task.skipConfig.conditionLogic ?? 'and',
            skipMode: task.skipConfig.skipMode,
          } : undefined,
          subtasks: Array.isArray(task.subtasks) ? task.subtasks.map(formatImportedTask) : [],
        });

        const formattedTasks: Task[] = importData.tasks.map(formatImportedTask);

        // Navigate with imported tasks and linked_workflows (if present in export)
        navigate(`/dashboard/${orgId}/create-workflow-v4`, {
          state: {
            copiedTasks: formattedTasks,
            importedTitle: importData.title || 'Imported Chatbot',
            importedTemplate: importData.template || 'sop',
            linkedWorkflows: importData.linked_workflows,
          }
        });

        toast({
          title: "Import Successful",
          description: "Chatbot configuration imported successfully",
          status: "success",
          duration: 3000,
        });

        onClose();
      } catch (error) {
        toast({
          title: "Import Failed",
          description: error instanceof Error ? error.message : "Failed to parse the JSON file",
          status: "error",
          duration: 5000,
        });
      }
    };

    reader.onerror = () => {
      toast({
        title: "Read Error",
        description: "Failed to read the file",
        status: "error",
        duration: 3000,
      });
    };

    reader.readAsText(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={'Create New Chatbot'}
      size="lg"
    >
      <VStack spacing={6} align="stretch">
        {/* Copy from Existing Chatbot Section */}
        <FormControl>
          <FormLabel
            fontSize="sm"
            fontWeight="medium"
            color="text-primary"
          >
            Copy from Existing Chatbot (Optional)
          </FormLabel>
          <Select
            value={selectedChatbotId}
            onChange={(e) => setSelectedChatbotId(e.target.value)}
            placeholder="Select a chatbot to copy tasks from"
            isDisabled={isLoading}
            bg="bg-surface"
            borderColor="border-default"
            _hover={{ borderColor: "border-hover" }}
            _focus={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
            mb={4}
          >
            {chatbotsData?.items.map((bot) => (
              <option key={bot.id} value={bot.id}>
                {bot.title}
              </option>
            ))}
          </Select>
        </FormControl>

        {/* Export Section */}
        {selectedChatbotId && (
          <Box>
            <Button
              height="auto"
              p={{ base: 4, md: 6 }}
              onClick={handleExport}
              variant="ghost"
              _hover={{
                bg: "interactive-hover",
                transform: "translateY(-2px)",
                shadow: "md",
              }}
              transition="all 0.2s"
              width="100%"
              display="block"
              isDisabled={isSelectedLoading || isLoading}
            >
              <HStack
                spacing={4}
                align="flex-start"
                width="100%"
              >
                <Icon
                  as={MdDownload}
                  boxSize={{ base: 5, md: 6 }}
                  color="blue.500"
                  flexShrink={0}
                />
                <Box flex={1}>
                  <Text
                    fontWeight="bold"
                    color="text-primary"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                    textAlign="left"
                    noOfLines={1}
                  >
                    Export Selected Chatbot
                  </Text>
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    color="text-secondary"
                    textAlign="left"
                    noOfLines={3}
                    wordBreak="break-word"
                    whiteSpace="pre-wrap"
                  >
                    Download the selected chatbot configuration as a JSON file
                  </Text>
                </Box>
              </HStack>
            </Button>
          </Box>
        )}

        <Divider borderColor="border-muted" />

        {/* Import Section */}
        <Box>
          <FormLabel
            fontSize="sm"
            fontWeight="medium"
            color="text-primary"
            mb={3}
          >
            Import from File
          </FormLabel>
          <Button
            height="auto"
            p={{ base: 4, md: 6 }}
            onClick={handleImport}
            variant="ghost"
            _hover={{
              bg: "interactive-hover",
              transform: "translateY(-2px)",
              shadow: "md",
            }}
            transition="all 0.2s"
            width="100%"
            display="block"
            isDisabled={isLoading}
          >
            <HStack
              spacing={4}
              align="flex-start"
              width="100%"
            >
              <Icon
                as={MdUpload}
                boxSize={{ base: 5, md: 6 }}
                color="orange.500"
                flexShrink={0}
              />
              <Box flex={1}>
                <Text
                  fontWeight="bold"
                  color="text-primary"
                  mb={1}
                  fontSize={{ base: "sm", md: "md" }}
                  textAlign="left"
                  noOfLines={1}
                >
                  Import from JSON File
                </Text>
                <Text
                  fontSize={{ base: "xs", md: "sm" }}
                  color="text-secondary"
                  textAlign="left"
                  noOfLines={3}
                  wordBreak="break-word"
                  whiteSpace="pre-wrap"
                >
                  Upload a JSON file to create a new chatbot from an exported configuration
                </Text>
              </Box>
            </HStack>
          </Button>
          <Input
            ref={fileInputRef}
            data-testid="wfl-import-file-input"
            type="file"
            accept=".json"
            onChange={handleFileChange}
            display="none"
          />
        </Box>

        <Divider borderColor="border-muted" />

        {/* Create Button (V2 - default) */}
        <Button
          height="auto"
          p={{ base: 4, md: 6 }}
          onClick={() => handleMethodSelect(`/dashboard/${orgId}/create-workflow-v4`)}
          variant="ghost"
          _hover={{
            bg: "interactive-hover",
            transform: "translateY(-2px)",
            shadow: "md",
          }}
          transition="all 0.2s"
          width="100%"
          display="block"
          isDisabled={isSelectedLoading || isLoading}
        >
          <HStack
            spacing={4}
            align="flex-start"
            width="100%"
          >
            <Icon
              as={selectedChatbotId ? MdContentCopy : MdTableChart}
              boxSize={{ base: 5, md: 6 }}
              color="green.500"
              flexShrink={0}
            />
            <Box flex={1}>
              <HStack spacing={2} mb={1}>
                <Text
                  fontWeight="bold"
                  color="text-primary"
                  fontSize={{ base: "sm", md: "md" }}
                  textAlign="left"
                  noOfLines={1}
                >
                  {selectedChatbotId ? "Create Copy" : "Create New Chatbot"}
                </Text>
                <Badge colorScheme="green" fontSize="2xs">New</Badge>
              </HStack>
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                color="text-secondary"
                textAlign="left"
                noOfLines={3}
                wordBreak="break-word"
                whiteSpace="pre-wrap"
              >
                {selectedChatbotId
                  ? "Create a new chatbot using tasks from the selected chatbot as a template"
                  : "Create a new chatbot from scratch"}
              </Text>
            </Box>
          </HStack>
        </Button>

        {canUseAI && (
          <>
            <Divider borderColor="border-muted" />

            {/* Use AI */}
            <Button
              height="auto"
              p={{ base: 4, md: 6 }}
              onClick={() => handleMethodSelect(`/dashboard/${orgId}/create-workflow-v4?ai=1`)}
              variant="ghost"
              _hover={{
                bg: "interactive-hover",
                transform: "translateY(-2px)",
                shadow: "md",
              }}
              transition="all 0.2s"
              width="100%"
              display="block"
              isDisabled={isSelectedLoading || isLoading}
            >
              <HStack
                spacing={4}
                align="flex-start"
                width="100%"
              >
                <Icon
                  as={FiCpu}
                  boxSize={{ base: 5, md: 6 }}
                  color="purple.500"
                  flexShrink={0}
                />
                <Box flex={1}>
                  <Text
                    fontWeight="bold"
                    color="text-primary"
                    mb={1}
                    fontSize={{ base: "sm", md: "md" }}
                    textAlign="left"
                    noOfLines={1}
                  >
                    Use AI
                  </Text>
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    color="text-secondary"
                    textAlign="left"
                    noOfLines={3}
                    wordBreak="break-word"
                    whiteSpace="pre-wrap"
                  >
                    Describe the workflow you want in natural language and let AI build it
                  </Text>
                </Box>
              </HStack>
            </Button>
          </>
        )}
      </VStack>
    </Modal>
  );
};
